import React, { useMemo, useState } from 'react';
import {
    Drawer, Box, Typography, IconButton, CircularProgress, Alert,
    ToggleButton, ToggleButtonGroup, Chip, Divider,
} from '@mui/material';
import { X, Activity, AlertTriangle, RefreshCw } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import StatPill from './StatPill';
import { useHostDetails } from '../hooks/useInfraHealth';

const WINDOW_OPTIONS = [
    { value: 3600, label: '1h' },
    { value: 6 * 3600, label: '6h' },
    { value: 24 * 3600, label: '24h' },
    { value: 7 * 24 * 3600, label: '7d' },
];

const SEV_LABEL = ['Not classified', 'Info', 'Warning', 'Average', 'High', 'Disaster'];
const SEV_TONE = ['muted', 'info', 'warn', 'warn', 'crit', 'crit'];

function fmtUnits(v, units) {
    if (v == null || !Number.isFinite(v)) return '—';
    const u = (units || '').trim();
    if (u === 'B' || u === 'Bps') {
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let i = 0, x = Math.abs(v);
        while (x >= 1024 && i < units.length - 1) { x /= 1024; i++; }
        return `${(v < 0 ? -x : x).toFixed(x >= 100 ? 0 : 1)} ${units[i]}${u === 'Bps' ? '/s' : ''}`;
    }
    if (u === '%') return `${v.toFixed(1)}%`;
    if (u === 's' || u === 'uptime') return `${v.toFixed(0)}s`;
    if (Math.abs(v) >= 1000) return `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}${u ? ' ' + u : ''}`;
    return `${v.toFixed(v >= 10 ? 1 : 3)}${u ? ' ' + u : ''}`;
}

export default function HostDetailDrawer({ open, host, onClose }) {
    const [windowSec, setWindowSec] = useState(3600);
    const { data, loading, error, refresh } = useHostDetails(open && host ? host.host : null, windowSec);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 620, md: 780, lg: 920 },
                    background: 'linear-gradient(160deg, rgba(15,23,42,0.98), rgba(30,41,59,0.98))',
                    borderLeft: '1px solid rgba(148,163,184,0.2)',
                    color: '#e2e8f0',
                },
            }}
        >
            <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <Header host={host} data={data} loading={loading} onClose={onClose} refresh={refresh} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1.5, flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={windowSec}
                        onChange={(_e, v) => v && setWindowSec(v)}
                        sx={{
                            '& .MuiToggleButton-root': {
                                textTransform: 'none',
                                fontSize: 12, px: 1.5, py: 0.35,
                                color: 'rgba(226,232,240,0.7)',
                                borderColor: 'rgba(148,163,184,0.2)',
                                '&.Mui-selected': { color: '#60a5fa', bgcolor: 'rgba(59,130,246,0.12)' },
                            },
                        }}
                    >
                        {WINDOW_OPTIONS.map(w => (
                            <ToggleButton key={w.value} value={w.value}>{w.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    {data?.aggregated && (
                        <Chip size="small" label="trends" sx={{ fontSize: 10.5, height: 20, bgcolor: 'rgba(148,163,184,0.15)', color: '#cbd5e1' }} />
                    )}
                    {data && (
                        <Typography sx={{ fontSize: 11, opacity: 0.6, ml: 'auto' }}>
                            {data.graphs.length} of {data.totalGraphs} graphs
                        </Typography>
                    )}
                </Box>

                {error && (
                    <Alert severity="warning" sx={{ mb: 1.5, bgcolor: 'rgba(239,68,68,0.08)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)' }}>
                        Failed to load: {error.message || 'unknown error'}
                    </Alert>
                )}

                {data && <HostMeta data={data} />}

                <Box sx={{ flex: 1, overflowY: 'auto', mt: 1, pr: 0.5, mx: -0.5, px: 0.5 }}>
                    {loading && !data && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {data && data.graphs.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 5, opacity: 0.6, fontSize: 13 }}>
                            No graphs defined for this host in Zabbix.
                        </Box>
                    )}
                    {data && data.graphs.map(g => (
                        <GraphPanel key={g.graphid} graph={g} windowSec={windowSec} />
                    ))}
                </Box>
            </Box>
        </Drawer>
    );
}

function Header({ host, data, loading, onClose, refresh }) {
    const online = host?.available === 1
        || host?.metrics?.agentPing === 1
        || host?.metrics?.icmpPing === 1
        || host?.metrics?.icmpReachable === 1;
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.1, opacity: 0.6, fontWeight: 600 }}>
                    Host · Zabbix detail
                </Typography>
                <Typography sx={{ fontSize: { xs: 20, sm: 23 }, fontWeight: 700, lineHeight: 1.15, mt: 0.25, wordBreak: 'break-all' }}>
                    {data?.name || host?.name || host?.host || '—'}
                </Typography>
                <Typography sx={{ fontSize: 12, opacity: 0.7, mt: 0.35 }}>
                    {host?.host}{host?.ip ? ` · ${host.ip}` : ''}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StatPill tone={online ? 'good' : 'crit'} icon={<Activity size={11} />}>{online ? 'Up' : 'Down'}</StatPill>
                <IconButton onClick={refresh} size="small" sx={{ color: '#cbd5e1' }} disabled={loading} title="Refresh">
                    <RefreshCw size={16} />
                </IconButton>
                <IconButton onClick={onClose} size="small" sx={{ color: '#cbd5e1' }} title="Close">
                    <X size={18} />
                </IconButton>
            </Box>
        </Box>
    );
}

function HostMeta({ data }) {
    const { templates = [], groups = [], problems = [], interfaces = [] } = data;
    const iface = interfaces.find(i => i.main === '1') || interfaces[0];
    return (
        <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, background: 'rgba(15,23,42,0.55)', border: '1px solid rgba(148,163,184,0.12)' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: problems.length ? 1 : 0 }}>
                {iface && (
                    <Chip size="small" label={`${iface.ip}:${iface.port || ''}`} sx={{ fontSize: 10.5, height: 20, bgcolor: 'rgba(148,163,184,0.12)', color: '#cbd5e1' }} />
                )}
                {templates.map(t => (
                    <Chip key={t} size="small" label={t} sx={{ fontSize: 10.5, height: 20, bgcolor: 'rgba(99,102,241,0.16)', color: '#a5b4fc' }} />
                ))}
                {groups.map(g => (
                    <Chip key={g} size="small" label={g} sx={{ fontSize: 10.5, height: 20, bgcolor: 'rgba(16,185,129,0.14)', color: '#6ee7b7' }} />
                ))}
            </Box>
            {problems.length > 0 && (
                <>
                    <Divider sx={{ borderColor: 'rgba(148,163,184,0.1)', my: 0.75 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {problems.slice(0, 5).map(p => (
                            <Box key={p.eventid} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 11.5 }}>
                                <AlertTriangle size={12} style={{ color: '#fbbf24', flexShrink: 0 }} />
                                <StatPill tone={SEV_TONE[p.severity]} size="sm">{SEV_LABEL[p.severity]}</StatPill>
                                <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>{p.name}</Typography>
                            </Box>
                        ))}
                        {problems.length > 5 && (
                            <Typography sx={{ fontSize: 11, opacity: 0.55 }}>…and {problems.length - 5} more</Typography>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
}

function GraphPanel({ graph, windowSec }) {
    const chart = useMemo(() => buildChartConfig(graph, windowSec), [graph, windowSec]);
    if (!chart) return null;
    return (
        <Box sx={{
            mt: 1.5,
            p: 1.25, pt: 1,
            borderRadius: 2,
            background: 'rgba(15,23,42,0.55)',
            border: '1px solid rgba(148,163,184,0.12)',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.2 }}>{graph.name}</Typography>
                <Typography sx={{ fontSize: 10.5, opacity: 0.55, fontVariantNumeric: 'tabular-nums' }}>
                    {graph.items.length} series
                </Typography>
            </Box>
            <Box sx={{ height: 180 }}>
                <Line data={chart.data} options={chart.options} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
                {graph.items.map(it => {
                    const lastV = parseFloat(it.lastValue);
                    const lastStr = Number.isFinite(lastV) ? fmtUnits(lastV, it.units) : (it.lastValue || '—');
                    return (
                        <Box key={it.itemid} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: 99, background: it.color, boxShadow: `0 0 6px ${it.color}` }} />
                            <Typography sx={{ fontSize: 11, opacity: 0.85 }}>{it.name}</Typography>
                            <Typography sx={{ fontSize: 11, opacity: 0.55, fontVariantNumeric: 'tabular-nums' }}>· {lastStr}</Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function buildChartConfig(graph, windowSec) {
    const items = graph.items.filter(it => it.series && it.series.length > 0);
    if (items.length === 0) return null;
    const stacked = graph.graphtype === 1;  // stacked
    const units = items[0].units || '';
    const hasPercent = units === '%';

    const datasets = items.map(it => ({
        label: it.name,
        data: it.series.map(p => ({ x: p.t, y: p.v })),
        borderColor: it.color,
        backgroundColor: stacked ? it.color + '40' : it.color + '20',
        fill: stacked ? (it === items[0] ? 'origin' : '-1') : false,
        tension: 0.25,
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3,
        spanGaps: true,
    }));

    const timeUnit = windowSec <= 3600 ? 'minute' : windowSec <= 24 * 3600 ? 'hour' : 'day';

    return {
        data: { datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'nearest', axis: 'x', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: 'rgba(15,23,42,0.95)',
                    borderColor: 'rgba(148,163,184,0.3)',
                    borderWidth: 1,
                    titleColor: '#e2e8f0',
                    bodyColor: '#cbd5e1',
                    callbacks: {
                        label: (ctx) => `${ctx.dataset.label}: ${fmtUnits(ctx.parsed.y, units)}`,
                    },
                },
            },
            scales: {
                x: {
                    type: 'time',
                    time: { unit: timeUnit },
                    ticks: { color: 'rgba(226,232,240,0.55)', maxRotation: 0, autoSkip: true, maxTicksLimit: 6, font: { size: 10 } },
                    grid: { color: 'rgba(148,163,184,0.08)' },
                },
                y: {
                    stacked,
                    beginAtZero: hasPercent,
                    max: hasPercent ? 100 : undefined,
                    ticks: {
                        color: 'rgba(226,232,240,0.55)',
                        font: { size: 10 },
                        callback: (v) => fmtUnits(v, units),
                    },
                    grid: { color: 'rgba(148,163,184,0.08)' },
                },
            },
        },
    };
}
